import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MatchCard from "../../../../utils/tournament/matchCard";
import { toast } from "react-toastify";
import { HiPlus, HiTrash } from "react-icons/hi";
import { jwtDecode } from "jwt-decode";

const MatchesTab = ({ tournament, selectedSubcategory }) => {
  const token = useSelector((state) => state.user.token);
  const [fechas, setFechas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [isOwner, setIsOwner] = useState(false);

  // Modales
  const [showCreateMatchModal, setShowCreateMatchModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedFechaId, setSelectedFechaId] = useState(null);

  // Configuración de fechas
  const [fechasConfig, setFechasConfig] = useState({
    numberOfFechas: "",
    isDoubleRound: false,
    autoCalculate: true,
  });

  // Generación de partidos
  const [generateMode, setGenerateMode] = useState("all");
  const [selectedFechas, setSelectedFechas] = useState([]);

  // Crear partido manual
  const [newMatch, setNewMatch] = useState({
    team1_id: "",
    team2_id: "",
    team1_category_id: null,
    team2_category_id: null,
    match_date: "",
    fecha_id: "",
    sede_id: null,
  });

  // Decodificar token y verificar ownership
  useEffect(() => {
    if (token && tournament?.user_id) {
      try {
        const decodedToken = jwtDecode(token);
        setUserId(decodedToken.id);
        setIsOwner(decodedToken.id === tournament.user_id);
      } catch (error) {
        console.error("Error decodificando token:", error);
        setIsOwner(false);
      }
    } else {
      setIsOwner(false);
    }
  }, [token, tournament?.user_id]);

  // Obtener fechas y partidos desde el backend
  const fetchFechas = async () => {
    if (!tournament?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = selectedSubcategory
        ? `${process.env.REACT_APP_API_URL}/api/fechas/tournaments/${tournament.id}/fechas?subcategoryId=${selectedSubcategory.id}`
        : `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/fechas`;

      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Error al obtener fechas");
      }

      const data = await response.json();
      setFechas(data);
    } catch (err) {
      console.error("Error fetching fechas:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Obtener equipos para los modales (solo dueño)
  const fetchTeams = async () => {
    if (!isOwner || !token) return;

    try {
      const url = selectedSubcategory
        ? `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams?subcategoryId=${selectedSubcategory.id}`
        : `${process.env.REACT_APP_API_URL}/api/tournaments/${tournament.id}/teams`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Error al obtener equipos");
      const data = await response.json();
      setTeams(data);
    } catch (error) {
      toast.error("Error al cargar equipos");
      console.error(error);
    }
  };

  // Cargar fechas cuando cambie el torneo o subcategoría
  useEffect(() => {
    if (tournament?.id) {
      fetchFechas();
    }
  }, [tournament?.id, selectedSubcategory?.id]);

  // ----- MANEJO DE FECHAS (CONFIGURACIÓN) - SOLO DUEÑO -----
  const handleCreateFechas = async () => {
    if (!isOwner) return;

    try {
      const url = `${process.env.REACT_APP_API_URL}/api/fechas/tournaments/${tournament.id}/fechas`;
      const body = {
        subcategoryId: selectedSubcategory?.id || null,
        numberOfFechas: fechasConfig.autoCalculate
          ? null
          : Number(fechasConfig.numberOfFechas),
        isDoubleRound: fechasConfig.isDoubleRound,
        autoCalculate: fechasConfig.autoCalculate,
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Error al crear fechas");
      toast.success("Fechas configuradas correctamente");
      await fetchFechas();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ----- GENERACIÓN DE PARTIDOS (ALEATORIA) - SOLO DUEÑO -----
  const handleGenerateMatches = async () => {
    if (!isOwner) return;

    try {
      if (fechas.length === 0) {
        await handleCreateFechas();
        await new Promise((resolve) => setTimeout(resolve, 500));
        await fetchFechas();
      }

      const url = `${process.env.REACT_APP_API_URL}/api/matches/generate-for-fechas`;
      const body = {
        tournamentId: tournament.id,
        subcategoryId: selectedSubcategory?.id || null,
        allFechas: generateMode === "all",
        fechaIds: generateMode === "selected" ? selectedFechas : [],
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Error al generar partidos");
      }

      toast.success(data.message || "Partidos generados con éxito");
      setShowGenerateModal(false);
      await fetchFechas();
    } catch (err) {
      toast.error(err.message);
      console.error(err);
    }
  };

  // ----- CREACIÓN MANUAL DE PARTIDO - SOLO DUEÑO -----
  const openCreateMatchModal = (fechaId) => {
    if (!isOwner) return;

    setSelectedFechaId(fechaId);
    setNewMatch({
      ...newMatch,
      fecha_id: fechaId,
      sede_id: null,
    });
    fetchTeams();
    setShowCreateMatchModal(true);
  };

  const handleCreateMatch = async () => {
    if (!isOwner) return;

    if (!newMatch.team1_id || !newMatch.team2_id || !newMatch.fecha_id) {
      toast.error("Completa todos los campos obligatorios");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matches`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            tournament_id: tournament.id,
            subcategory_id: selectedSubcategory?.id || null,
            team1_id: newMatch.team1_id,
            team2_id: newMatch.team2_id,
            team1_category_id: newMatch.team1_category_id,
            team2_category_id: newMatch.team2_category_id,
            fecha_id: newMatch.fecha_id,
            match_date: newMatch.match_date || null,
            sede_id: newMatch.sede_id || null,
            is_subcategory_match: !!selectedSubcategory,
          }),
        },
      );

      if (!response.ok) throw new Error("Error al crear partido");

      toast.success("Partido creado");
      setShowCreateMatchModal(false);
      setNewMatch({
        team1_id: "",
        team2_id: "",
        team1_category_id: null,
        team2_category_id: null,
        match_date: "",
        fecha_id: "",
        sede_id: null,
      });
      setSelectedFechaId(null);
      await fetchFechas();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (matchId) => {
    if (!isOwner) return;
    if (!window.confirm("¿Seguro que quieres eliminar este partido?")) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matches/${matchId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!response.ok) throw new Error("Error al eliminar");
      toast.success("Partido eliminado");
      await fetchFechas();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateMatch = async (updatedMatch) => {
    if (!isOwner) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/matches/${updatedMatch.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updatedMatch),
        },
      );

      if (!response.ok) throw new Error("Error al actualizar");
      toast.success("Partido actualizado");
      await fetchFechas();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ----- RENDER -----
  if (loading) {
    return (
      <div className="bg-gray-900 text-white p-4 rounded-lg">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Cargando partidos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-900 text-white p-4 rounded-lg">
        <div className="text-center text-red-500 p-8">
          <p>Error: {error}</p>
          <button
            onClick={fetchFechas}
            className="mt-4 px-4 py-2 bg-emerald-500 rounded-lg hover:bg-emerald-600"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white p-4 rounded-lg">
      <h2 className="text-lg font-bold mb-4">
        Partidos del torneo {tournament.tournament_name}
      </h2>

      {/* Botón solo visible para el dueño */}
      {isOwner && (
        <div className="flex flex-wrap gap-3 mb-6">
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 rounded-xl bg-blue-500 text-white font-semibold shadow hover:bg-blue-600 transition"
          >
            Configurar y Generar Partidos
          </button>
        </div>
      )}

      {/* Lista de fechas (jornadas) */}
      {fechas.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-8 text-center">
          <p className="text-gray-400">
            No hay fechas configuradas.
            {isOwner && ' Usa "Configurar y Generar Partidos" para empezar.'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {fechas.map((fecha) => (
            <div
              key={fecha.id}
              className="bg-gray-800 rounded-xl p-4 relative group"
            >
              {/* Contenedor del encabezado */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-semibold text-emerald-400">
                    Fecha {fecha.fecha_number}
                    {/* Mostramos la fecha si existe, pero sin input de edición */}
                    {fecha.date && (
                      <span className="text-sm text-gray-400 ml-2">
                        ({new Date(fecha.date).toLocaleDateString()})
                      </span>
                    )}
                  </h3>
                  {/* Botón de agregar partido solo para dueño */}
                  {isOwner && (
                    <button
                      onClick={() => openCreateMatchModal(fecha.id)}
                      className="p-1 bg-purple-500 hover:bg-purple-600 rounded-full transition-colors"
                      title="Agregar partido manualmente"
                    >
                      <HiPlus size={20} />
                    </button>
                  )}
                </div>

                {/* Botón para eliminar la fecha completa (solo dueño) */}
              </div>

              {/* Partidos de la fecha */}
              {fecha.matches.length === 0 ? (
                <p className="text-gray-400 italic">Por definirse</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fecha.matches.map((match) => (
                    <MatchCard
                      key={match.id}
                      match={match}
                      onDelete={isOwner ? handleDelete : null}
                      onEdit={isOwner ? handleUpdateMatch : null}
                      selectedSubcategory={selectedSubcategory}
                      isOwner={isOwner}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de crear partido - solo dueño */}
      {showCreateMatchModal && isOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md text-gray-800">
            <h3 className="text-xl font-bold mb-4">
              Crear Nuevo Partido - Fecha{" "}
              {fechas.find((f) => f.id === selectedFechaId)?.fecha_number}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Equipo Local</label>
                <select
                  className="w-full p-2 rounded border"
                  value={
                    newMatch.team1_id
                      ? JSON.stringify({
                          team_id: newMatch.team1_id,
                          team_category_id: newMatch.team1_category_id,
                        })
                      : ""
                  }
                  onChange={(e) => {
                    const obj = JSON.parse(e.target.value);
                    setNewMatch({
                      ...newMatch,
                      team1_id: obj.team_id,
                      team1_category_id: obj.team_category_id,
                    });
                  }}
                >
                  <option value="">Seleccionar equipo</option>
                  {teams.map((team) => (
                    <option
                      key={team.team_category_id}
                      value={JSON.stringify({
                        team_id: team.team_id,
                        team_category_id: team.team_category_id,
                      })}
                    >
                      {team.team_name} - {team.group_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium">
                  Equipo Visitante
                </label>
                <select
                  className="w-full p-2 rounded border"
                  value={
                    newMatch.team2_id
                      ? JSON.stringify({
                          team_id: newMatch.team2_id,
                          team_category_id: newMatch.team2_category_id,
                        })
                      : ""
                  }
                  onChange={(e) => {
                    const obj = JSON.parse(e.target.value);
                    setNewMatch({
                      ...newMatch,
                      team2_id: obj.team_id,
                      team2_category_id: obj.team_category_id,
                    });
                  }}
                >
                  <option value="">Seleccionar equipo</option>
                  {teams.map((team) => (
                    <option
                      key={team.team_category_id}
                      value={JSON.stringify({
                        team_id: team.team_id,
                        team_category_id: team.team_category_id,
                      })}
                    >
                      {team.team_name} - {team.group_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium">
                  Fecha y hora (opcional)
                </label>
                <input
                  type="datetime-local"
                  className="w-full p-2 rounded border"
                  value={newMatch.match_date}
                  onChange={(e) =>
                    setNewMatch({ ...newMatch, match_date: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateMatchModal(false);
                  setSelectedFechaId(null);
                  setNewMatch({
                    team1_id: "",
                    team2_id: "",
                    team1_category_id: null,
                    team2_category_id: null,
                    match_date: "",
                    fecha_id: "",
                    sede_id: null,
                  });
                }}
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateMatch}
                className="px-4 py-2 rounded-xl bg-emerald-500 text-white font-semibold hover:bg-emerald-600"
              >
                Crear Partido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de configuración y generación - solo dueño */}
      {showGenerateModal && isOwner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md text-gray-800 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">
              Configurar y Generar Partidos
            </h3>
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h4 className="font-semibold mb-3 text-lg">
                  Configuración de Fechas
                </h4>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    checked={fechasConfig.autoCalculate}
                    onChange={(e) =>
                      setFechasConfig({
                        ...fechasConfig,
                        autoCalculate: e.target.checked,
                      })
                    }
                  />
                  Calcular automáticamente según número de equipos
                </label>
                {!fechasConfig.autoCalculate && (
                  <div className="mb-3">
                    <label className="block mb-1 font-medium">
                      Número de fechas
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={fechasConfig.numberOfFechas}
                      onChange={(e) =>
                        setFechasConfig({
                          ...fechasConfig,
                          numberOfFechas: e.target.value,
                        })
                      }
                      className="w-full p-2 rounded border"
                    />
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={fechasConfig.isDoubleRound}
                    onChange={(e) =>
                      setFechasConfig({
                        ...fechasConfig,
                        isDoubleRound: e.target.checked,
                      })
                    }
                  />
                  Torneo de ida y vuelta (doble ronda)
                </label>
              </div>
              <div>
                <h4 className="font-semibold mb-3 text-lg">
                  Generación de Partidos
                </h4>
                <div className="mb-3">
                  <label className="block font-medium mb-2">
                    Modo de generación
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="generateMode"
                        value="all"
                        checked={generateMode === "all"}
                        onChange={() => setGenerateMode("all")}
                      />
                      Todas las fechas
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="generateMode"
                        value="selected"
                        checked={generateMode === "selected"}
                        onChange={() => setGenerateMode("selected")}
                      />
                      Fechas específicas
                    </label>
                  </div>
                </div>
                {generateMode === "selected" && fechas.length > 0 && (
                  <div>
                    <label className="block font-medium mb-2">
                      Selecciona las fechas
                    </label>
                    <div className="max-h-40 overflow-y-auto space-y-2 border rounded p-2">
                      {fechas.map((f) => (
                        <label key={f.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedFechas.includes(f.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedFechas([...selectedFechas, f.id]);
                              } else {
                                setSelectedFechas(
                                  selectedFechas.filter((id) => id !== f.id),
                                );
                              }
                            }}
                          />
                          Fecha {f.fecha_number}{" "}
                          {f.date &&
                            `(${new Date(f.date).toLocaleDateString()})`}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {fechas.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Nota: Se crearán las fechas según la configuración antes de
                    generar los partidos.
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleGenerateMatches}
                disabled={
                  generateMode === "selected" &&
                  selectedFechas.length === 0 &&
                  fechas.length > 0
                }
                className="px-4 py-2 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-600 disabled:opacity-50"
              >
                {fechas.length === 0
                  ? "Configurar y Generar"
                  : "Generar Partidos"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesTab;
